/**
 * The voice cap (ADR-085 decision 10): at most a number of sounds at once and at most a number of one hook, a new sound
 * past either cap taking a playing voice by priority and level, or dropped. A voice may have an owner, the thing whose
 * sound it plays, so a thing's new sound can find and stop its earlier one (the amendment of 2026-10-13), and a voice's
 * level may be rewritten as the mixer follows it, so the order reads what is heard now. The table is preallocated
 * typed arrays and allocates nothing as it is used.
 */

/**
 * How much a sound matters when voices run out: the interface and stingers first, then the page's own character's
 * sounds, then positional sounds by level.
 */
export const VoicePriority = { Positional: 0, Own: 1, Interface: 2 } as const;
export type VoicePriority = (typeof VoicePriority)[keyof typeof VoicePriority];

/**
 * No voice: the sound is dropped.
 */
export const NO_VOICE = -1;

/**
 * No owner: the sound is nobody's in particular, and no later sound stops it for being of the same thing.
 */
export const NO_OWNER = -1;

/**
 * The voices and what each plays.
 */
export class VoiceTable {
  private readonly active: Uint8Array;
  private readonly hooks: Int32Array;
  private readonly priorities: Uint8Array;
  private readonly levels: Float32Array;
  private readonly started: Float64Array;
  private readonly owners: Int32Array;
  private count = 0;

  /**
   * @param size How many sounds may play at once.
   * @param perHook How many of one hook may play at once.
   */
  public constructor(
    size: number,
    private readonly perHook: number,
  ) {
    this.active = new Uint8Array(size);
    this.hooks = new Int32Array(size);
    this.priorities = new Uint8Array(size);
    this.levels = new Float32Array(size);
    this.started = new Float64Array(size);
    this.owners = new Int32Array(size).fill(NO_OWNER);
  }

  /**
   * How many voices play.
   *
   * @returns The count.
   */
  public get playing(): number {
    return this.count;
  }

  /**
   * How many voices play a hook.
   *
   * @param hook The hook's number.
   * @returns The count.
   */
  public playingOf(hook: number): number {
    let playing = 0;
    for (let index = 0; index < this.active.length; index += 1) {
      if (this.active[index] === 1 && this.hooks[index] === hook) {
        playing += 1;
      }
    }
    return playing;
  }

  /**
   * The voice a new sound would take, without taking it: past the per-hook cap, the hook's oldest voice; otherwise a
   * free voice; otherwise the voice of lowest priority, then lowest level, then oldest, among those of lower priority
   * or of equal priority and no higher level.
   *
   * @param hook The hook's number.
   * @param priority The sound's priority.
   * @param level The sound's level, 0 to 1.
   * @returns The voice's index, or `NO_VOICE` to drop the sound.
   */
  public claim(hook: number, priority: VoicePriority, level: number): number {
    if (this.playingOf(hook) >= this.perHook) {
      return this.oldestOf(hook);
    }
    const free = this.active.indexOf(0);
    if (free !== -1) {
      return free;
    }
    let best = NO_VOICE;
    for (let index = 0; index < this.active.length; index += 1) {
      const held = this.priorities[index] as number;
      const heldLevel = this.levels[index] as number;
      if (held > priority || (held === priority && heldLevel > level)) {
        continue;
      }
      if (best === NO_VOICE || this.ranksBelow(index, best)) {
        best = index;
      }
    }
    return best;
  }

  /**
   * Marks a voice as playing a sound, in place of whatever it played.
   *
   * @param voice The voice's index, from `claim`.
   * @param hook The hook's number.
   * @param priority The sound's priority.
   * @param level The sound's level.
   * @param now The time.
   * @param owner Whose sound it is, a whole number of 0 or more; `NO_OWNER` by default.
   */
  public start(voice: number, hook: number, priority: VoicePriority, level: number, now: number, owner: number = NO_OWNER): void {
    if (this.active[voice] === 0) {
      this.count += 1;
    }
    this.active[voice] = 1;
    this.hooks[voice] = hook;
    this.priorities[voice] = priority;
    this.levels[voice] = level;
    this.started[voice] = now;
    this.owners[voice] = owner;
  }

  /**
   * The voice an owner's sound plays on.
   *
   * @param owner The owner.
   * @returns The voice's index, or `NO_VOICE` when the owner holds none or is `NO_OWNER`.
   */
  public heldBy(owner: number): number {
    if (owner === NO_OWNER) {
      return NO_VOICE;
    }
    for (let index = 0; index < this.active.length; index += 1) {
      if (this.active[index] === 1 && this.owners[index] === owner) {
        return index;
      }
    }
    return NO_VOICE;
  }

  /**
   * Takes a playing voice from its owner because its sound is being faded out: it keeps playing and counting, has
   * no owner, and its level is 0, so it gives way first when voices run out.
   *
   * @param voice The voice's index.
   */
  public disown(voice: number): void {
    this.owners[voice] = NO_OWNER;
    this.levels[voice] = 0;
  }

  /**
   * Rewrites the level a playing voice is heard at, as the mixer follows it. A free voice is left alone.
   *
   * @param voice The voice's index.
   * @param level The level, 0 to 1.
   */
  public setLevel(voice: number, level: number): void {
    if (this.active[voice] === 1) {
      this.levels[voice] = level;
    }
  }

  /**
   * Frees a voice whose sound ended or was stopped.
   *
   * @param voice The voice's index.
   */
  public release(voice: number): void {
    if (this.active[voice] === 1) {
      this.active[voice] = 0;
      this.count -= 1;
    }
    this.owners[voice] = NO_OWNER;
  }

  /**
   * The oldest voice playing a hook.
   *
   * @param hook The hook's number.
   * @returns The voice's index, or `NO_VOICE` when none plays it.
   */
  private oldestOf(hook: number): number {
    let oldest = NO_VOICE;
    for (let index = 0; index < this.active.length; index += 1) {
      if (this.active[index] === 1 && this.hooks[index] === hook && (oldest === NO_VOICE || (this.started[index] as number) < (this.started[oldest] as number))) {
        oldest = index;
      }
    }
    return oldest;
  }

  /**
   * Whether one voice gives way before another: lower priority, then lower level, then older.
   *
   * @param a One voice.
   * @param b The other.
   * @returns True when `a` gives way first.
   */
  private ranksBelow(a: number, b: number): boolean {
    const priorityA = this.priorities[a] as number;
    const priorityB = this.priorities[b] as number;
    if (priorityA !== priorityB) {
      return priorityA < priorityB;
    }
    const levelA = this.levels[a] as number;
    const levelB = this.levels[b] as number;
    if (levelA !== levelB) {
      return levelA < levelB;
    }
    return (this.started[a] as number) < (this.started[b] as number);
  }
}
